import { t } from "i18next";

type MessageData = {
  key: string;
  values: Record<string, any>;
};
type Gender = "m" | "f" | "n";

interface GeoForm {
  nominative: {
    singular: string;
    plural: string;
  };
  accusative: {
    singular: string;
    plural: string;
  };
  gender: Gender;
}

/**
 * useGeoSelectionMessage
 *
 * A custom React hook that generates a selection message for geographical entities
 * (e.g. "client", "company", etc.) based on the number of selected items and grammatical rules.
 *
 * It uses different grammatical cases and genders to return a correct and localized string.
 * Useful for gendered languages (e.g., Serbian, Russian, etc.) where word endings vary
 * depending on gender, number, and case.
 *
 * ### Logic:
 * - If `count === 0`: use `translations.selection.none` + accusative singular label
 * - If `count === 1`: use `translations.selection.one_${geo.gender}` + nominative singular label
 * - If `count > 1`: use `translations.selection.many` + count + accusative plural label
 *
 * @param count - Number of selected geographical items (e.g. selected clients, locations, etc.)
 * @param geoLabel - Object containing grammatical case forms and gender for the geo key
 * @param translations - Object with localized selection messages
 *
 * @returns A localized and grammatically correct message string
 */

function useGeoSelectionMessage() {
  return (geoKey: string, count: number): MessageData => {
    const geo = t(`statisticPage.header.filter.dropdown.geoFilters.${geoKey}`, {
      returnObjects: true,
      defaultValue: {
        nominative: { singular: geoKey, plural: geoKey },
        accusative: { singular: geoKey, plural: geoKey },
        gender: "n",
      },
    }) as GeoForm;

    if (count === 0) {
      return {
        key: "statisticPage.header.filter.dropdown.selection.none",
        values: { label: geo.accusative.singular },
      };
    }

    if (count === 1) {
      return {
        key: `statisticPage.header.filter.dropdown.selection.one_${geo.gender}`,
        values: { count, label: geo.nominative.singular },
      };
    }

    return {
      key: "statisticPage.header.filter.dropdown.selection.many",
      values: { count, label: geo.accusative.plural },
    };
  };
}

export default useGeoSelectionMessage;
